/*
 * Copyright (c) CIRROM 2026.
 */

import React, { useEffect, useState } from "react";
import {
  Box,
  CardHeader,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { MiraCard, MiraCardContent } from "../mira/components/common/Card";
import {
  ContactMonitorChangesMadeStats,
  getChangesMadeStatsForProjectOrSystem,
} from "../../api/cirrommonitor";

interface ContactMonitorDataOverviewProps {
  isDataProject: boolean;
  dataProjectId: number;
  integratedSystemId: number;
}

export const ContactMonitorDataOverview: React.FC<ContactMonitorDataOverviewProps> =
  ({ isDataProject, dataProjectId, integratedSystemId }) => {
    const [stats, setStats] = useState<ContactMonitorChangesMadeStats | null>(
      null
    );
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
      if (integratedSystemId) {
        setIsLoading(true);
        getChangesMadeStatsForProjectOrSystem(
          !isDataProject ? integratedSystemId : undefined,
          isDataProject ? dataProjectId : undefined
        )
          .then((response) => setStats(response))
          .catch((error) => console.log(error))
          .finally(() => setIsLoading(false));
      }
    }, [integratedSystemId, isDataProject, dataProjectId]);

    if (isLoading) {
      return (
        <MiraCard
          style={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
          <CardHeader title={"Monitor Changes Made"} />
          <MiraCardContent
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <CircularProgress />
            </Box>
          </MiraCardContent>
        </MiraCard>
      );
    }

    return (
      <MiraCard
        style={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <CardHeader title={"Monitor Changes Made"} />
        <MiraCardContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Table
            size="small"
            sx={{
              "& tr:last-child td, & tr:last-child th": { borderBottom: 0 },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell align="right">
                  <Typography
                    variant={"caption"}
                    style={{ fontWeight: "bold" }}
                  >
                    Contacts
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell component="th" scope="row">
                  <Typography
                    variant={"caption"}
                    style={{ fontWeight: "bold" }}
                  >
                    Total
                  </Typography>
                </TableCell>
                <TableCell align="right">{stats?.total ?? 0}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  <Typography
                    variant={"caption"}
                    style={{ fontWeight: "bold" }}
                  >
                    Name
                  </Typography>
                </TableCell>
                <TableCell align="right">{stats?.name ?? 0}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  <Typography
                    variant={"caption"}
                    style={{ fontWeight: "bold" }}
                  >
                    Job Title
                  </Typography>
                </TableCell>
                <TableCell align="right">{stats?.job_title ?? 0}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  <Typography
                    variant={"caption"}
                    style={{ fontWeight: "bold" }}
                  >
                    Company Movers
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {stats?.company_movers ?? 0}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  <Typography
                    variant={"caption"}
                    style={{ fontWeight: "bold" }}
                  >
                    City
                  </Typography>
                </TableCell>
                <TableCell align="right">{stats?.city ?? 0}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  <Typography
                    variant={"caption"}
                    style={{ fontWeight: "bold" }}
                  >
                    Country
                  </Typography>
                </TableCell>
                <TableCell align="right">{stats?.country ?? 0}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </MiraCardContent>
      </MiraCard>
    );
  };
